import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ALERT_STATUS_LABELS,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TYPE_LABELS,
  INVOICE_BASIS_LABELS,
  INVOICE_OUTCOME_LABELS,
  INVOICE_PAYMENT_LABELS,
  INVOICE_STATUS_LABELS,
  MILESTONE_STATUS_LABELS,
  PAYMENT_TERMS_ISSUE_LABELS,
  PAYMENT_TERMS_ISSUE_SHORT_LABELS,
  SEVERITY_LABELS,
  VALIDATION_STATUS_LABELS,
  formatCurrency,
  formatCurrencyCompact,
  formatDate,
  formatHours,
  formatPercent,
  invoicePaymentState,
  timeEntryStatusLabel,
  type AlertStatus,
  type ContractStatus,
  type ContractType,
  type InvoiceBasis,
  type InvoicePaymentState,
  type InvoiceSettlement,
  type InvoiceStatus,
  type PaymentTermsIssue,
  type MilestoneStatus,
  type Severity,
  type ValidationStatus,
} from '@codepass/shared';
import { Badge, Button, Card, type BadgeTone } from '@/components/ui';
import { cn } from '@/lib/cn';

/**
 * Domaenespecifikke visningskomponenter.
 *
 * Farve er aldrig eneste baerer af betydning: hvert badge har ogsaa sin danske tekst.
 * Det gaelder isaer alvorsgrad, hvor farven ellers ville vaere hele budskabet.
 */

const CONTRACT_STATUS_TONE: Record<ContractStatus, BadgeTone> = {
  draft: 'neutral',
  awaiting_signature: 'warning',
  active: 'success',
  completed: 'brand',
  cancelled: 'neutral',
  expired: 'danger',
};

const VALIDATION_TONE: Record<ValidationStatus, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  invoiced: 'brand',
};

const INVOICE_TONE: Record<InvoiceStatus, BadgeTone> = {
  draft: 'neutral',
  approved: 'warning',
  sent_to_bc: 'info',
  posted: 'success',
  failed: 'danger',
  cancelled: 'neutral',
};

const MILESTONE_TONE: Record<MilestoneStatus, BadgeTone> = {
  planned: 'neutral',
  in_progress: 'info',
  reached: 'warning',
  invoiced: 'success',
  cancelled: 'neutral',
};

const ALERT_TONE: Record<AlertStatus, BadgeTone> = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  snoozed: 'neutral',
  dismissed: 'neutral',
};

const SEVERITY_TONE: Record<Severity, BadgeTone> = {
  info: 'info',
  warning: 'warning',
  critical: 'danger',
};

export function ContractStatusBadge({ status }: { status: ContractStatus }) {
  return <Badge tone={CONTRACT_STATUS_TONE[status]}>{CONTRACT_STATUS_LABELS[status]}</Badge>;
}

export function ContractTypeBadge({ type }: { type: ContractType }) {
  return <Badge tone="neutral">{CONTRACT_TYPE_LABELS[type]}</Badge>;
}

export function ValidationStatusBadge({ status }: { status: ValidationStatus }) {
  return <Badge tone={VALIDATION_TONE[status]}>{VALIDATION_STATUS_LABELS[status]}</Badge>;
}

/*
 * `HoursValidationBadge` er fjernet sammen med valideringen af timer. Den viste vaerdien fra
 * public.codepass_hours_status paa fanerne Godkendte og Afviste; begge er vaek, og der er
 * ingen tilstand tilbage at vise - se hovedkommentaren i apps/api/src/routes/time-entries.ts.
 */

/**
 * OnePro's opgavestatus. Ukendte vaerdier faar neutral tone og vises raat - se
 * timeEntryStatusLabel. Farven er aldrig eneste baerer af betydning; teksten staar der.
 */
const TIME_ENTRY_TONE: Record<string, BadgeTone> = {
  'Not Started': 'neutral',
  'In Progress': 'info',
  Waiting: 'warning',
  QA: 'info',
  Testing: 'info',
  'Ready for invoicing': 'success',
  'Partially Ready for Invoicing': 'success',
  'Not Billable': 'neutral',
  Internal: 'neutral',
  Sales: 'brand',
  /* Codepass' egen: opgaven er paa en faktura i BC. Se INVOICED_STATUS i @codepass/shared. */
  Invoiced: 'brand',
};

export function TimeEntryStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-ink-muted">—</span>;
  return <Badge tone={TIME_ENTRY_TONE[status] ?? 'neutral'}>{timeEntryStatusLabel(status)}</Badge>;
}

/**
 * Betalt green and the unpaid blue, so the two posted states tell apart at a glance - see
 * `invoicePaymentState`. An overdue invoice says so in its due date, not here.
 */
const PAYMENT_TONE: Record<InvoicePaymentState, BadgeTone> = {
  paid: 'success',
  partly_paid: 'warning',
  unpaid: 'info',
};

/**
 * A customer's payment terms: "Netto 8 dage" - and, when that is the default because Business
 * Central has none for the customer, says so in amber, with the whole sentence on hover. See
 * `PaymentTermsIssue`.
 */
export function PaymentTerms({ days, issue }: { days: number; issue?: PaymentTermsIssue | null }) {
  return (
    <>
      Netto {days} dage
      {issue ? (
        <span className="text-amber-700" title={PAYMENT_TERMS_ISSUE_LABELS[issue]}>
          {' '}
          · {PAYMENT_TERMS_ISSUE_SHORT_LABELS[issue]}
        </span>
      ) : null}
    </>
  );
}

/**
 * The line under DSO on the dashboard and /kunder/:id: what the days are from. The actual days to
 * pay from BC's application entries, or an estimate that must say so - see `daysToPay` and OQ-10.
 */
export function dsoHint(estimated: boolean, paymentCount: number | null | undefined): string {
  if (paymentCount === null || paymentCount === undefined) {
    return estimated ? 'Estimeret ud fra betalingsbetingelserne' : 'Ingen betalinger endnu';
  }
  if (paymentCount === 0) return 'Ingen betalinger de seneste 12 måneder';
  const payments = paymentCount === 1 ? '1 betaling' : `${paymentCount} betalinger`;
  return estimated
    ? `Estimeret · ${payments} — udligningerne i BC kunne ikke læses`
    : `Fra bogføring til betaling · ${payments} de seneste 12 måneder`;
}

/** The payments and credits on an invoice, one per line - the badge's tooltip. */
function settlementTitle(settlement: InvoiceSettlement, currency?: string): string | undefined {
  const lines = settlement.applications
    .filter((application) => application.amount > 0)
    .map(
      (application) =>
        `${application.kind === 'credit' ? 'Krediteret' : 'Betalt'} ${formatDate(application.date)}` +
        ` · ${formatCurrency(application.amount, currency)}` +
        (application.document_no ? ` · ${application.document_no}` : ''),
    );
  if (!settlement.exact && lines.length > 0) {
    lines.push('Estimeret: udligningerne i Business Central kunne ikke læses.');
  }
  return lines.length > 0 ? lines.join('\n') : undefined;
}

/**
 * The status of an invoice. A posted one shows its payment: Betalt, Delvist betalt or Bogført from
 * BC's own status, read fresh with the invoice - and, from the customer ledger, the day it was paid
 * under the badge, or Krediteret when a credit memo closed it (BC calls that Paid too). The
 * tooltip lists the payments with their documents.
 */
export function InvoiceStatusBadge({
  status,
  payment,
}: {
  status: InvoiceStatus;
  /**
   * BC's payment of a posted invoice - status, what is left and the total, and the ledger's
   * `settlement`. Given, a posted invoice says how it is paid; left out, the status alone.
   */
  payment?: {
    bc_status?: string | null;
    remaining_amount?: number;
    amount_incl_vat: number;
    currency?: string;
    settlement?: InvoiceSettlement | null;
  };
}) {
  const state = payment ? invoicePaymentState({ status, ...payment }) : null;
  if (!state) return <Badge tone={INVOICE_TONE[status]}>{INVOICE_STATUS_LABELS[status]}</Badge>;

  const settlement = payment?.settlement ?? null;
  const credited = state === 'paid' && settlement?.outcome === 'credited';
  const title =
    (settlement ? settlementTitle(settlement, payment?.currency) : undefined) ??
    (state === 'partly_paid' && payment?.remaining_amount !== undefined
      ? `Restbeløb ${formatCurrency(payment.remaining_amount, payment.currency)}`
      : undefined);
  const date = credited ? settlement?.closed_date : settlement?.paid_date;

  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <Badge tone={credited ? 'neutral' : PAYMENT_TONE[state]} title={title}>
        {credited ? INVOICE_OUTCOME_LABELS.credited : INVOICE_PAYMENT_LABELS[state]}
      </Badge>
      {date && state === 'paid' ? (
        <span className="text-xs text-ink-muted tabular" title={title}>
          {formatDate(date)}
          {settlement && !settlement.exact ? ' (est.)' : ''}
        </span>
      ) : null}
    </span>
  );
}

export function InvoiceBasisBadge({ basis }: { basis: InvoiceBasis }) {
  return <Badge tone="neutral">{INVOICE_BASIS_LABELS[basis]}</Badge>;
}

export function MilestoneStatusBadge({ status }: { status: MilestoneStatus }) {
  return <Badge tone={MILESTONE_TONE[status]}>{MILESTONE_STATUS_LABELS[status]}</Badge>;
}

export function AlertStatusBadge({ status }: { status: AlertStatus }) {
  return <Badge tone={ALERT_TONE[status]}>{ALERT_STATUS_LABELS[status]}</Badge>;
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  return <Badge tone={SEVERITY_TONE[severity]}>{SEVERITY_LABELS[severity]}</Badge>;
}

/** Alvorsgradens farve som venstrekant paa alarmkort. */
export const SEVERITY_BORDER: Record<Severity, string> = {
  info: 'border-l-info',
  warning: 'border-l-warning',
  critical: 'border-l-danger',
};

/**
 * Initialerne bag et navn. Foerste bogstav i foerste og sidste ord - "Benjamin Kongshøj
 * Hansen" bliver til BH, ikke BKH, saa der altid er plads i cirklen.
 */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';

  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Profilbillede med initialer som reserve.
 *
 * Reserven er ikke en fejltilstand, men det normale: billedet hentes fra Microsoft, og
 * langtfra alle har sat et. Derfor er der ingen tom plads og intet ikon for "mangler" -
 * initialerne er en fuldgyldig visning, og raekkerne staar lige uanset hvad.
 *
 * `onError` daekker det tilfaelde hvor der *er* en URL, men billedet ikke kan hentes: en
 * udloebet delingslink, en bruger der er fjernet i Entra. Uden den ville browseren vise
 * sit eget ikon for et braekket billede.
 */
export function Avatar({
  name,
  url,
  className,
}: {
  name: string | null | undefined;
  url?: string | null;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const label = name?.trim() || 'Ukendt';

  return (
    <span
      className={cn(
        'inline-flex h-6 w-6 shrink-0 select-none items-center justify-center overflow-hidden',
        'rounded-full bg-brand-50 text-[10px] font-semibold text-brand-800',
        className,
      )}
      // Navnet staar ved siden af i tabellen, saa billedet er dekoration for en
      // skaermlaeser. Uden dette ville navnet blive laest op to gange pr. raekke.
      aria-hidden
      title={label}
    >
      {url && !failed ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        initials(label)
      )}
    </span>
  );
}

/** Konsulentens profilbillede med navnet ved siden af. Bruges i timetabellerne. */
export function ConsultantCell({
  consultant,
}: {
  consultant: { name: string | null; avatar_url?: string | null } | null;
}) {
  if (!consultant?.name) return <span className="text-ink-muted">—</span>;

  return (
    <span className="flex items-center gap-2">
      <Avatar name={consultant.name} url={consultant.avatar_url} />
      <span className="truncate">{consultant.name}</span>
    </span>
  );
}

/**
 * A link only where the role can follow it. Otherwise the same text, without the hover styling
 * that says it can be clicked: a link that ends on /forbudt, or on a page the API refuses, is
 * worse than none. `allowed` comes from the permission helpers - `canOpenContract`,
 * `canOpenInvoice`, `can`.
 */
export function PermittedLink({
  to,
  allowed,
  className,
  target,
  rel,
  children,
}: {
  to: string;
  allowed: boolean;
  className?: string;
  target?: string;
  rel?: string;
  children: ReactNode;
}) {
  if (allowed) {
    return (
      <Link to={to} className={className} target={target} rel={rel}>
        {children}
      </Link>
    );
  }
  const plain = className
    ?.split(' ')
    .filter((name) => !name.startsWith('hover:') && name !== 'underline')
    .join(' ');
  return <span className={plain || undefined}>{children}</span>;
}

export function Money({
  value,
  currency = 'DKK',
  className,
  decimals,
}: {
  value: number | null | undefined;
  currency?: string;
  className?: string;
  decimals?: number;
}) {
  return (
    <span className={cn('tabular', className)}>
      {formatCurrency(value, currency, decimals === undefined ? {} : { decimals })}
    </span>
  );
}

export function HoursValue({
  value,
  className,
}: {
  value: number | null | undefined;
  className?: string;
}) {
  return <span className={cn('tabular', className)}>{formatHours(value)}</span>;
}

export function Percent({
  value,
  className,
}: {
  value: number | null | undefined;
  className?: string;
}) {
  return <span className={cn('tabular', className)}>{formatPercent(value)}</span>;
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold">{title}</h1>
        {description ? <p className="mt-1 text-xs text-ink-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * Hent listen igen. Staar i PageHeaderens `actions` paa de sider, der viser data fra et
 * fremmed system.
 *
 * Findes fordi de lister ikke opdaterer sig selv: kontrakter kommer fra Dynamics, kunder og
 * fakturaer fra Business Central, og en aftale oprettet derude dukker ikke op af sig selv.
 * Klienten har 30 sekunders `staleTime` og henter **ikke** ved vinduesfokus (se main.tsx), saa
 * uden en knap var vejen til nye raekker at genindlaese hele siden.
 *
 * `Timer` har ingen knap, og skal ikke have en: den henter selv paa interval - se Hours.tsx.
 *
 * `prefixes` er foerste led i de query-noegler, siden lever af. Praefikset frem for hele
 * noeglen, fordi listerne noegles med filtre og offset (`['contracts', filter, offset]`), og en
 * opdatering skal ramme dem alle - ogsaa de sider af tabellen, man ikke staar paa.
 */
export function RefreshButton({
  prefixes,
  label = 'Opdater',
}: {
  prefixes: readonly string[];
  label?: string;
}) {
  const queryClient = useQueryClient();

  /*
   * Spinderen foelger **enhver** hentning paa de noegler, ikke kun den knappen selv startede.
   * Saa viser den ogsaa noget, naar man skifter side i tabellen - og to klik i traek ser ud
   * som ét arbejde frem for to.
   */
  const fetching =
    useIsFetching({
      predicate: (query) => prefixes.includes(String(query.queryKey[0])),
    }) > 0;

  return (
    <Button
      size="sm"
      variant="secondary"
      loading={fetching}
      onClick={() => {
        for (const prefix of prefixes) {
          void queryClient.invalidateQueries({ queryKey: [prefix] });
        }
      }}
    >
      {fetching ? null : <RefreshCw className="h-3.5 w-3.5" aria-hidden />}
      {label}
    </Button>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <p className="text-sm font-medium">{title}</p>
      {description ? <p className="max-w-md text-xs text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function MetricCard({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'danger' | 'warning' | 'success';
}) {
  const toneClass = {
    neutral: 'text-ink',
    danger: 'text-danger',
    warning: 'text-amber-700',
    success: 'text-emerald-700',
  }[tone];

  return (
    <div className="card p-4">
      <p className="text-xs text-ink-muted">{label}</p>
      <p className={cn('mt-1 text-xl font-semibold tabular', toneClass)}>{value}</p>
      {hint ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * Vandrette søjler med en etiket til venstre og tallet til højre.
 *
 * 'Fakturering pr. måned' og 'Top 5 kunder' på dashboardet, og månedsoversigten på en
 * serviceaftales Timer-fane. Én komponent, så de ser ens ud — og så en måned på kontrakten læses
 * som en måned på dashboardet.
 *
 * `format` er tallets formatering; standarden er kroner i kort form. Med `onSelect` bliver hver
 * søjle en knap, og `selected` fremhæver den valgte — Timer-fanen filtrerer på den måde.
 *
 * `reference` is a line across the bar - the month's budget on the dashboard - with its value
 * under the bar's own. The scale includes it, so a bar short of its line looks short. A `value`
 * of null is no figure yet - a month that has not begun - and shows an empty bar and a dash.
 */
export function BarChart({
  items,
  format = formatCurrencyCompact,
  selected,
  onSelect,
}: {
  items: Array<{ key?: string; label: string; value: number | null; reference?: number | null }>;
  format?: (value: number) => string;
  selected?: string | null;
  onSelect?: (key: string) => void;
}) {
  const max = Math.max(...items.flatMap((item) => [item.value ?? 0, item.reference ?? 0]), 1);

  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const key = item.key ?? item.label;
        const active = selected === key;
        const content = (
          <>
            <span className="w-24 shrink-0 truncate text-xs text-ink-muted" title={item.label}>
              {item.label}
            </span>
            <span className="relative h-5 flex-1 overflow-hidden rounded bg-canvas">
              <span
                className={cn(
                  'block h-full rounded',
                  selected && !active ? 'bg-brand/40' : 'bg-brand',
                )}
                style={{
                  width: item.value === null ? 0 : `${Math.max(2, (item.value / max) * 100)}%`,
                }}
              />
              {typeof item.reference === 'number' ? (
                <span
                  aria-hidden
                  className="absolute inset-y-0 w-0.5 bg-ink"
                  style={{ left: `calc(${Math.min(100, (item.reference / max) * 100)}% - 1px)` }}
                />
              ) : null}
            </span>
            <span className="w-24 shrink-0 text-right text-xs tabular">
              {item.value === null ? '—' : format(item.value)}
              {typeof item.reference === 'number' ? (
                <span className="block text-[11px] text-ink-muted">
                  af {format(item.reference)}
                </span>
              ) : null}
            </span>
          </>
        );

        return (
          <li key={key}>
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(key)}
                aria-pressed={active}
                className={cn(
                  'flex w-full items-center gap-3 rounded px-1 text-left hover:bg-canvas',
                  active && 'bg-canvas font-medium',
                )}
              >
                {content}
              </button>
            ) : (
              <div className="flex items-center gap-3">{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="field-label">{label}</span>
      {children}
    </div>
  );
}

/**
 * The filter card beside a list - on /kontrakter and /timer.
 *
 * **Folded below `lg`**, where the card sits above the table instead of beside it: open, its
 * eight fields would push the table a whole phone screen down. The heading is the button, and
 * says when a filter is set, so a folded panel does not hide that the list is filtered. From `lg`
 * up it is always open and looks as before.
 */
export function FilterPanel({
  title,
  active,
  action,
  children,
}: {
  title: ReactNode;
  /** A filter is set. Shown on the heading while the panel is folded. */
  active: boolean;
  /** Next to the heading - the reset button. */
  action?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1 lg:pointer-events-none"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {title}
          {active && !open ? (
            <span className="text-xs font-normal text-brand-800 lg:hidden">· aktive</span>
          ) : null}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-ink-muted transition-transform lg:hidden',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
        {action}
      </div>
      <div className={cn('space-y-3', !open && 'hidden lg:block')}>{children}</div>
    </Card>
  );
}

export function Pagination({
  total,
  limit,
  offset,
  onChange,
}: {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}) {
  if (total <= limit) return null;

  const from = offset + 1;
  const to = Math.min(offset + limit, total);

  return (
    <div className="flex items-center justify-between border-t border-line px-3 py-2 text-xs text-ink-muted">
      <span>
        Viser {from}–{to} af {total}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          className="rounded border border-line px-2 py-1 disabled:opacity-40"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Forrige
        </button>
        <button
          type="button"
          className="rounded border border-line px-2 py-1 disabled:opacity-40"
          disabled={to >= total}
          onClick={() => onChange(offset + limit)}
        >
          Næste
        </button>
      </div>
    </div>
  );
}
